import { SheetTextInput } from "@/components/ui/sheet-text-input";
import { BrandColors, Colors } from "@/constants/theme";
import { useColorScheme } from "@/hooks/use-color-scheme";
import { VehicleCheckData } from "@/lib/services/vehicle-check";
import { getRemoteImageSource } from "@/lib/utils/media-url";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import { useMemo, useState } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { ThemedText } from "../themed-text";
import { Label } from "./label";
import { VehicleStatusRow } from "./vehicle-status-box";

interface VehicleRegistrationInputProps {
  registration: string | undefined;
  onChangeRegistration: (text: string) => void;
  isChecking: boolean;
  error: string | null;
  vehicleData: VehicleCheckData | null;
  vehicleCategory: string | undefined;
  inBottomSheet?: boolean;
  /**
   * Render only the registration input (`input`) or only the vehicle details
   * card (`details`) so they can be placed in separate columns.
   */
  part?: "all" | "input" | "details";
  /** Controlled editing state, shared between `input` and `details` parts. */
  isEditing?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
}

export function VehicleRegistrationInput({
  registration,
  onChangeRegistration,
  isChecking,
  error,
  vehicleData,
  vehicleCategory,
  inBottomSheet = false,
  part = "all",
  isEditing: controlledIsEditing,
  onEditingChange,
}: VehicleRegistrationInputProps) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing = controlledIsEditing ?? internalIsEditing;
  const setIsEditing = onEditingChange ?? setInternalIsEditing;
  const [imageError, setImageError] = useState(false);
  const imageSource = useMemo(
    () => getRemoteImageSource(vehicleData?.vehicle_image_url),
    [vehicleData?.vehicle_image_url],
  );

  const showVehicleImage = imageSource !== null && !imageError;

  if (!vehicleData && internalIsEditing) {
    setInternalIsEditing(false);
  }

  const showCollapsed = vehicleData && !isEditing;
  const showInput = part === "input" || (part === "all" && !showCollapsed);
  const showDetails = part !== "input" && !!vehicleData;

  const InputComponent = inBottomSheet ? SheetTextInput : TextInput;

  return (
    <View>
      {showInput && (
        <View style={styles.inputGroup}>
          <Label label="Vehicle Registration" />
          <View
            style={[
              styles.registrationInputContainer,
              {
                borderColor: theme.border,
                backgroundColor: theme.background,
              },
            ]}
          >
            <View style={styles.registrationBadge}>
              <ThemedText
                style={styles.registrationBadgeText}
                type="defaultSemiBold"
              >
                GB
              </ThemedText>
            </View>
            <InputComponent
              style={[
                styles.registrationInput,
                { color: theme.text, backgroundColor: theme.background },
              ]}
              placeholder="ENTER REGISTRATION"
              placeholderTextColor="#9ca3af"
              value={registration}
              onChangeText={(text) => onChangeRegistration(text.toUpperCase())}
              autoCapitalize="characters"
              maxLength={10}
            />
            {isChecking && (
              <View style={styles.checkingIndicator}>
                <ActivityIndicator size="small" color={BrandColors.primary} />
              </View>
            )}
          </View>
        </View>
      )}

      {error && part !== "details" && (
        <View style={styles.errorCard}>
          <Ionicons name="alert-circle" size={20} color="#FF9500" />
          <ThemedText style={[styles.errorText, { flex: 1 }]} type="small">
            {error}
          </ThemedText>
        </View>
      )}

      {showDetails && vehicleData && (
        <View
          style={[
            styles.vehicleDetailsCard,
            part === "details" && styles.vehicleDetailsCardStandalone,
            { backgroundColor: theme.background, borderColor: theme.border },
          ]}
        >
          <View style={styles.vehicleDetailsHeader}>
            <View style={styles.verifiedBadge}>
              <View style={styles.verifiedDot} />
              <ThemedText style={styles.verifiedText} type="extraSmallSemiBold">
                Verified Vehicle
              </ThemedText>
            </View>
            {showCollapsed && (
              <TouchableOpacity
                style={styles.editButton}
                onPress={() => setIsEditing(true)}
              >
                <Ionicons
                  name="create-outline"
                  size={14}
                  color={BrandColors.primary}
                />
                <ThemedText
                  style={styles.editButtonText}
                  type="extraSmallSemiBold"
                >
                  Edit
                </ThemedText>
              </TouchableOpacity>
            )}
          </View>

          <View style={styles.vehicleDetailsBody}>
            <View style={styles.vehicleImageContainer}>
              {showVehicleImage ? (
                <Image
                  source={imageSource}
                  style={styles.vehicleImage}
                  contentFit="contain"
                  recyclingKey={imageSource?.uri}
                  onError={() => setImageError(true)}
                />
              ) : (
                <Image
                  source={require("@/assets/images/car.png")}
                  style={styles.vehicleImage}
                  contentFit="contain"
                />
              )}
            </View>

            <View style={styles.vehicleDetailsContent}>
              <ThemedText style={styles.vehicleMainTitle} type="smallSemiBold">
                {vehicleData.make} {vehicleData.model}
              </ThemedText>

              <View style={styles.vehicleInfoGrid}>
                <View
                  style={[
                    styles.vehicleInfoItem,
                    {
                      backgroundColor: theme.background,
                      borderColor: theme.border,
                    },
                  ]}
                >
                  <ThemedText style={styles.vehicleInfoLabel} type="extraSmall">
                    Year
                  </ThemedText>
                  <ThemedText
                    style={styles.vehicleInfoValue}
                    type="smallSemiBold"
                  >
                    {vehicleData.year || "-"}
                  </ThemedText>
                </View>
                <View
                  style={[
                    styles.vehicleInfoItem,
                    {
                      backgroundColor: theme.background,
                      borderColor: theme.border,
                    },
                  ]}
                >
                  <ThemedText style={styles.vehicleInfoLabel} type="extraSmall">
                    Color
                  </ThemedText>
                  <ThemedText
                    style={styles.vehicleInfoValue}
                    type="smallSemiBold"
                  >
                    {vehicleData.colour?.toUpperCase() || "-"}
                  </ThemedText>
                </View>
                <View
                  style={[
                    styles.vehicleInfoItem,
                    {
                      backgroundColor: theme.background,
                      borderColor: theme.border,
                    },
                  ]}
                >
                  <ThemedText style={styles.vehicleInfoLabel} type="extraSmall">
                    Fuel
                  </ThemedText>
                  <ThemedText
                    style={styles.vehicleInfoValue}
                    type="smallSemiBold"
                  >
                    {vehicleData.fuel_type?.toUpperCase() || "-"}
                  </ThemedText>
                </View>
              </View>

              <View style={styles.vehicleBadgesRow}>
                <View
                  style={[styles.vehicleBadge, { borderColor: theme.border }]}
                >
                  <Ionicons
                    name="speedometer-outline"
                    size={12}
                    color="#6b7280"
                  />
                  <ThemedText style={styles.vehicleBadgeText} type="extraSmall">
                    {vehicleData.body_type || "SUV"}
                  </ThemedText>
                </View>
                <View
                  style={[styles.vehicleBadge, { borderColor: theme.border }]}
                >
                  <Ionicons name="calendar-outline" size={12} color="#6b7280" />
                  <ThemedText style={styles.vehicleBadgeText} type="extraSmall">
                    {vehicleData.weight} kg
                  </ThemedText>
                </View>
                <View
                  style={[styles.vehicleBadge, { borderColor: theme.border }]}
                >
                  <Ionicons name="car-outline" size={12} color="#6b7280" />
                  <ThemedText style={styles.vehicleBadgeText} type="extraSmall">
                    {vehicleData.fuel_type || "-"}
                  </ThemedText>
                </View>
              </View>
            </View>
          </View>

          <VehicleStatusRow
            motStatus={vehicleData.mot_status}
            motExpiryDate={vehicleData.mot_expiry_date}
            taxStatus={vehicleData.tax_status}
            taxDueDate={vehicleData.tax_due_date}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  inputGroup: {
    gap: 8,
  },
  registrationInputContainer: {
    flexDirection: "row",
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
  },
  registrationBadge: {
    backgroundColor: BrandColors.primary,
    width: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  registrationBadgeText: {
    color: "#fff",
    fontSize: 16,
  },
  registrationInput: {
    flex: 1,
    padding: 12,
    fontSize: 20,
    fontWeight: "600",
    letterSpacing: 2,
    textTransform: "uppercase",
  },
  checkingIndicator: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: [{ translateY: -10 }],
  },
  errorCard: {
    flex: 1,
    marginTop: 12,
    padding: 12,
    backgroundColor: "#FFF9E6",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FF9500",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  errorText: {
    fontSize: 16,
    color: "#64748b",
    textAlign: "center",
  },
  vehicleDetailsCard: {
    marginTop: 12,
    padding: 14,
    borderRadius: 14,
    borderWidth: 1,
    gap: 12,
  },
  vehicleDetailsCardStandalone: {
    marginTop: 0,
  },
  vehicleDetailsHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  verifiedBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  verifiedDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BrandColors.primary,
  },
  verifiedText: {
    fontSize: 12,
    fontWeight: "600",
    color: BrandColors.primary,
  },
  vehicleDetailsBody: {
    flexDirection: "row",
    gap: 12,
    alignItems: "flex-start",
  },
  vehicleImageContainer: {
    width: 88,
    height: 88,
    borderRadius: 12,
    backgroundColor: "#EEF2F6",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  vehicleImage: {
    width: 80,
    height: 56,
  },
  vehicleDetailsContent: {
    flex: 1,
    gap: 10,
  },
  vehicleMainTitle: {
    fontSize: 15,
    fontWeight: "700",
    color: "#111827",
    lineHeight: 20,
  },
  vehicleInfoGrid: {
    flexDirection: "row",
    gap: 6,
  },
  vehicleInfoItem: {
    flex: 1,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  vehicleInfoLabel: {
    fontSize: 9,
    marginBottom: 2,
    textTransform: "uppercase",
    fontWeight: "600",
    letterSpacing: 0.3,
  },
  vehicleInfoValue: {
    fontSize: 12,
    fontWeight: "700",
  },
  vehicleBadgesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  vehicleBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
    borderWidth: 1,
  },
  vehicleBadgeText: {
    fontSize: 10,
    fontWeight: "500",
  },
  collapsedContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
  },
  collapsedRegistrationText: {
    fontSize: 18,
    fontWeight: "700",
    letterSpacing: 1,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: BrandColors.primary,
    backgroundColor: "#fff",
  },
  editButtonText: {
    color: BrandColors.primary,
    fontSize: 12,
    fontWeight: "600",
  },
});
